import { firebaseApp } from '@/firebase'
import {
  ApolloClient,
  createHttpLink,
  fromPromise,
  InMemoryCache,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import 'firebase/auth'
import { getSdkApollo } from './apollo-requester'

const httpLink = createHttpLink({
  uri: import.meta.env.VUE_APP_GRAPHQL_URL,
})

const authLink = setContext(async (_, { headers }) => {
  const user = firebaseApp.auth().currentUser
  if (!user) {
    return { headers }
  }

  // getIdToken() reuses the cached id token and only goes to the network
  // (via the refresh token) when it has expired.
  const idtoken = await user.getIdToken()

  return {
    headers: {
      ...headers,
      idtoken,
    },
  }
})

// The backend signals auth failures with a 401 status, or a GraphQL error
// whose message mentions a revoked/expired/invalid token. 403 is deliberately
// excluded: it can mean resource-level authorization, which a token refresh
// won't fix.
function isAuthError(graphQLErrors, networkError) {
  if (networkError?.statusCode === 401) return true

  return graphQLErrors?.some((err) => {
    if (err.extensions?.code === 'UNAUTHENTICATED') return true
    const message = err.message?.toLowerCase() ?? ''
    return (
      message.includes('token') &&
      (message.includes('revoked') ||
        message.includes('expired') ||
        message.includes('invalid'))
    )
  })
}

// On an auth error, force-refresh the id token once and retry the request
// before letting the error propagate. The auth link picks up the refreshed
// token from the Firebase SDK cache when the operation is forwarded again.
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (!isAuthError(graphQLErrors, networkError)) {
      return
    }
    if (operation.getContext().authRetried) {
      return
    }

    const user = firebaseApp.auth().currentUser
    if (!user) {
      return
    }

    operation.setContext({ authRetried: true })
    return fromPromise(user.getIdToken(true).catch(() => null)).flatMap(
      (idtoken) => {
        if (!idtoken) {
          // The refresh failed, so a retry would resend the same stale
          // token — let the original error propagate instead.
          throw networkError ?? graphQLErrors?.[0] ?? new Error('Auth error')
        }
        return forward(operation)
      }
    )
  }
)

const authenticatedClient = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
  defaultOptions: {
    mutate: {
      fetchPolicy: 'no-cache',
    },
    query: {
      fetchPolicy: 'no-cache',
    },
    watchQuery: {
      fetchPolicy: 'no-cache',
    },
  },
})

export const apolloClient = getSdkApollo(authenticatedClient)

export default authenticatedClient
